import { useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { alpha } from "@mui/material/styles";
import { Box, MenuItem, Stack, IconButton, Popover } from "@mui/material";
import { RootState } from "../../../store";
import { setLanguage } from "../../../store/appUserReducer";
import { APP_LANGUAGE } from "../../../constants";
import { setCookie } from "../../../utils/cookie";

export default function LanguagePopover() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const appLanguage = useSelector((state: RootState) => state.appUser.language);
  const [open, setOpen] = useState(null);

  const LANGS = [
    {
      value: "en",
      label: t("English"),
      icon: "/assets/icons/ic_flag_en.svg",
    },
    {
      value: "ta",
      label: t("Tamil"),
      icon: "/assets/icons/ic_flag_ta.svg",
    },
    {
      value: "te",
      label: t("Telugu"),
      icon: "/assets/icons/ic_flag_te.svg",
    },
    {
      value: "ml",
      label: t("Malayalam"),
      icon: "/assets/icons/ic_flag_ml.svg",
    },
    {
      value: "kn",
      label: t("Kannada"),
      icon: "/assets/icons/ic_flag_kn.svg",
    },
    {
      value: "hi",
      label: t("Hindi"),
      icon: "/assets/icons/ic_flag_hi.svg",
    },
    {
      value: "bn",
      label: t("Bengali"),
      icon: "/assets/icons/ic_flag_bn.svg",
    },
    {
      value: "pa",
      label: t("Punjabi"),
      icon: "/assets/icons/ic_flag_pa.svg",
    },
    {
      value: "mr",
      label: t("Marathi"),
      icon: "/assets/icons/ic_flag_mr.svg",
    },
    {
      value: "gu",
      label: t("Gujarati"),
      icon: "/assets/icons/ic_flag_gu.svg",
    },
    {
      value: "ur",
      label: t("Urdu"),
      icon: "/assets/icons/ic_flag_ur.svg",
    },
    {
      value: "zh",
      label: t("Chinese"),
      icon: "/assets/icons/ic_flag_zh.svg",
    },
    {
      value: "es",
      label: t("Spanish"),
      icon: "/assets/icons/ic_flag_es.svg",
    },
    {
      value: "fr",
      label: t("French"),
      icon: "/assets/icons/ic_flag_fr.svg",
    },
    {
      value: "ja",
      label: t("Japanese"),
      icon: "/assets/icons/ic_flag_ja.svg",
    },
    {
      value: "ru",
      label: t("Russian"),
      icon: "/assets/icons/ic_flag_ru.svg",
    },
    {
      value: "de",
      label: t("German"),
      icon: "/assets/icons/ic_flag_de.svg",
    },
    {
      value: "it",
      label: t("Italian"),
      icon: "/assets/icons/ic_flag_it.svg",
    },
    {
      value: "ko",
      label: t("Korean"),
      icon: "/assets/icons/ic_flag_ko.svg",
    },
    {
      value: "ar",
      label: t("Arabic"),
      icon: "/assets/icons/ic_flag_ar.svg",
    },
  ];

  const currentLanguage = useMemo(() => {
    return LANGS.find((l) => l.value === appLanguage);
  }, [appLanguage, LANGS]);

  const handleOpen = (event: any) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const onSelect = async (lang: any) => {
    setOpen(null);
    try {
      await i18n.changeLanguage(lang.value);
      setCookie(APP_LANGUAGE, lang.value);
      dispatch(setLanguage(lang.value));
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open
            ? {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.focusOpacity,
                  ),
              }
            : {}),
        }}
      >
        <img
          style={{ height: "20px" }}
          src={currentLanguage?.icon}
          alt={currentLanguage?.label}
        />
      </IconButton>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            mt: 1.5,
            ml: 0.75,
            width: 180,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <Stack spacing={0.75}>
          {LANGS.map((option) => (
            <MenuItem
              key={option.value}
              selected={option.value === appLanguage}
              onClick={() => onSelect(option)}
            >
              <Box
                component="img"
                alt={option.label}
                src={option.icon}
                sx={{ width: 28, mr: 2 }}
              />

              {option.label}
            </MenuItem>
          ))}
        </Stack>
      </Popover>
    </>
  );
}
